import _, { max } from "lodash";
import $ from "jquery";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Dimmer, Divider, Header, Icon, Label, Loader, Message, Modal, Segment, Sidebar, Sticky, Tab, Table } from "semantic-ui-react";
import { hostAbsoluteUrl, restRequest } from "../../http";
import { DATA_TYPE_SCALAR, DATA_TYPE_ARRAY, DATA_TYPE_VECTOR, DATA_TYPE_OTHER, Measurement, MeasurementData, TestingFile, VectorData } from "../../models/testing_model";
import { VectorPlotComponent } from "./VectorPlotComponent";
import { VectorDataComponent } from "./VectorDataComponent";
import { ScalarOrArrayDataComponent } from "./ScalarOrArrayDataComponent";
import { ArrayPlotComponent } from "./ArrayPlotComponent";
import { ScalarPlotComponent } from "./ScalarPlotComponent";
import { FileStatusMark, StatusMark } from "./StatusMark";
import "../../assets/scss/components/TestingFile.scss";
import { ModalControlData, useModalControl, useRequest } from "../../hooks/commonHooks";
import { AnalyzedUnitEditor } from "../AnalyzedUnitEditor";
import { useCurrentUser } from "../../hooks/auth-hook";
import { notificationControl } from "../../controls/notification-control";
import { SaleData } from "../../models/rma_tracking_model";
import { useFormConnect } from "../../hooks/formHooks";
import { formatDateTime } from "../../utils";
import { nextTick } from "process";
import { dialogControl } from "../../controls/dialog-control";
import { TestingVisualSearchParameterContext, TestResultMissingPointsContext } from "../../context";

export function TestingFilePage(props: { match: any }) {
    const id = props.match.params.id - 0;
    const activeTabIndex = props.match.params.activeTabIndex;
    const measurementKey = props.match.params.measurementKey;

    const sFile = useState<TestingFile>();

    useEffect(() => {
        restRequest<TestingFile>(`testings/file/${id}`, null, 'get').then(sFile[1]);
    }, [id]);

    if (sFile[0]) {
        return <TestingFileComponent file={sFile[0]} height={window.innerHeight - 30} isSinglePage={true} activeTabIndex={activeTabIndex} measurementKey={measurementKey}></TestingFileComponent>;
    } else {
        return <Segment loading />
    }

}

export function TestingFileComponent(props: { file: TestingFile, height: number, isSinglePage?: boolean, noOperation?: boolean, activeTabIndex?: string, measurementKey?: string, onFileChange?: (file: TestingFile) => void }) {
    const currentUser = useCurrentUser();
    const [file, setFile] = useState<TestingFile>(props.file);
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext);

    const sMeasurement = useState<Measurement[]>([]);
    const sSelected = useState<Measurement>();
    const sActiveTabIndex = useState(_.isUndefined(props.activeTabIndex) ? parseInt(_.get(searchParameters, 'file_active_tab', '0') || '0') : parseInt(props.activeTabIndex));

    const [innerHeight, setInnerHeight] = useState(props.height);

    const sMeasurementData = useState<MeasurementData[]>([]);
    const sLoadedMeasurementId = useState<number>();
    const sLoading = useState(false);

    const sIsMissingPoints = useState(false);

    const titleRef = useRef<HTMLDivElement>(null);

    const waiveUnitEditModalControl = useModalControl<boolean>(false);

    const showGeneralModalContrl = useModalControl();

    const onlyFailRef = useRef(false);

    const selectedMeasurementKey = props.measurementKey || _.get(searchParameters, 'selected_measurement_key', '');

    useEffect(() => {
        setFile(props.file);
    }, [props.file]);

    useEffect(() => {
        if (sSelected[0] !== undefined) {
            setParentSearchParameter((ps: any) => ({ ...ps, selected_measurement_key: sSelected[0]?.measurement_key }))
        }
    }, [sSelected[0]])

    useLayoutEffect(() => {
        const onResize = () => {
            const $innerSecondElement = $(titleRef.current!).next().find('div:eq(1)');
            if ($innerSecondElement.length > 0) {
                const checkUntilStable = (prev: number) => {
                    const current = $innerSecondElement.offset()!.top;

                    if (prev === current) {
                        setInnerHeight(window.innerHeight - $innerSecondElement.offset()!.top - 14 * 5 - 5);
                    } else {
                        setTimeout(() => checkUntilStable(current), 200);
                    }
                }

                checkUntilStable(0);
            }
        }

        window.addEventListener('resize', onResize);

        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, [props.height]);

    useEffect(() => {
        if (file) {
            sLoading[1](true);
            restRequest<Measurement[]>(`testings/measurements/${file.id}`).then(sMeasurement[1]).finally(() => sLoading[1](false));
        } else {
            sMeasurement[1]([]);
        }
    }, [file.id])

    useEffect(() => {
        const s = sSelected[0];
        if (s) {
            if (!sMeasurement[0].find(m => m.id === s.id)) {
                sSelected[1](sMeasurement[0].find(m => m.measurement_key === s.measurement_key));
            } else {
                const measurementId = s.id;
                restRequest<MeasurementData[]>(`testings/measurement_data/${measurementId}`).then(data => {
                    const channelLimits: Map<number, MeasurementData["limits"]> = new Map();
                    let maxChannelIndex = 0;

                    data.forEach(d => {
                        try {
                            d.limits = JSON.parse(d.limits as any);
                        } catch (e) {
                            console.error(`error parsing limits: ${d.limits}`)
                        }
                        if (!d.limits) {
                            d.limits = {} as any;
                        }
                        try {
                            d.result = JSON.parse(d.result as any);
                        } catch (e) {
                            console.error(`error parsing result: ${d.result}`)
                        }
                        if (!d.index) {
                            // only the final result has limits
                            channelLimits.set(d.channel, d.limits);
                        } else {
                            //previous attempts result don't have limits
                            d.limits = channelLimits.get(d.channel)!;

                            if (d.index > maxChannelIndex) {
                                maxChannelIndex = d.index;
                            }
                        }

                        if (s.limit_unit && s.result_unit) {
                            if (('u' + s.limit_unit).toLowerCase() === s.result_unit.toLowerCase()) {
                                let converter;
                                if (s.data_type == DATA_TYPE_SCALAR) {
                                    converter = (v: number) => v * 1e+6;
                                } else if (s.data_type == DATA_TYPE_ARRAY) {
                                    converter = (v: number[]) => v.map(vv => vv * 1e+6);
                                } else if (s.data_type == DATA_TYPE_VECTOR) {
                                    converter = (v: VectorData) => ({ ...v, y_values: v.y_values.map(vv => vv * 1e+6) });
                                } else {
                                    converter = (v: any) => v;
                                }
                                d.limits.upper = d.limits.upper ? converter(d.limits.upper) : d.limits.upper;
                                d.limits.lower = d.limits.lower ? converter(d.limits.lower) : d.limits.lower;
                            } else if (('u' + s.result_unit).toLowerCase() === s.limit_unit.toLowerCase()) {
                                let converter;
                                if (s.data_type == DATA_TYPE_SCALAR) {
                                    converter = (v: number) => v * 1e+6;
                                } else if (s.data_type == DATA_TYPE_ARRAY) {
                                    converter = (v: number[]) => v.map(vv => vv * 1e+6);
                                } else if (s.data_type == DATA_TYPE_VECTOR) {
                                    converter = (v: VectorData) => ({ ...v, y_values: v.y_values.map(vv => vv * 1e+6) });
                                } else {
                                    converter = (v: any) => v;
                                }

                                d.result = d.result ? converter(d.result) : d.result;
                            }
                        }

                        if (s.data_type == DATA_TYPE_VECTOR) {
                            const result = d.result as VectorData;
                            if (result.x_values) {
                                const newXValues: number[] = [];
                                const newYValues: number[] = [];
                                result.y_values.forEach((y, i) => {
                                    if (!_.isNull(y) && !_.isNaN(y)) {
                                        newXValues.push(result.x_values[i]);
                                        newYValues.push(y);
                                    }
                                });

                                result.x_values = newXValues;
                                result.y_values = newYValues;
                            }
                        }
                    });

                    data.forEach(d => {
                        if (!d.index) {
                            d.index = maxChannelIndex + 1;
                        }
                    })

                    sMeasurementData[1](data);
                    sLoadedMeasurementId[1](measurementId);
                }).finally(() => sLoading[1](false));
            }
        } else {
            sSelected[1](sMeasurement[0].find(m => m.measurement_key == selectedMeasurementKey) || sMeasurement[0][0]);
        }
    }, [sMeasurement[0], sSelected[0]]);

    const onNextMeasurement = useCallback((onlyFail: boolean, isBack: boolean) => {
        if (sMeasurement[0].length > 0) {
            const selectedIndex = sSelected[0] ? sMeasurement[0].findIndex(m => m.id === sSelected[0]?.id) : -1;

            let nextIndex: number;
            if (selectedIndex === -1) {
                if (onlyFail) {
                    nextIndex = sMeasurement[0].findIndex(m => m.measurement_status === 2);
                    if (nextIndex === -1) {
                        notificationControl.showInfo('No fail measurement found', null, false, 1);
                        return;
                    }
                } else {
                    nextIndex = 0;
                }
                sSelected[1](sMeasurement[0][nextIndex]);
            } else {
                nextIndex = selectedIndex;
                while (true) {
                    if (!isBack) {
                        ++nextIndex;
                        if (nextIndex >= sMeasurement[0].length) {
                            notificationControl.showInfo(onlyFail ? 'No more fail until the end' : 'Has reach the end', null, false, 1);
                            break;
                        } else {
                            if (onlyFail) {
                                if (sMeasurement[0][nextIndex].measurement_status === 2) {
                                    sSelected[1](sMeasurement[0][nextIndex]);
                                    break;
                                }
                            } else {
                                sSelected[1](sMeasurement[0][nextIndex]);
                                break;
                            }
                        }
                    } else {
                        --nextIndex;
                        if (nextIndex < 0) {
                            notificationControl.showInfo(onlyFail ? 'No more fail until the top' : 'Has reach the top', null, false, 1);
                            break;
                        } else {
                            if (onlyFail) {
                                if (sMeasurement[0][nextIndex].measurement_status === 2) {
                                    sSelected[1](sMeasurement[0][nextIndex]);
                                    break;
                                }
                            } else {
                                sSelected[1](sMeasurement[0][nextIndex]);
                                break;
                            }
                        }
                    }
                }
            }
        }

    }, [sMeasurement[0], sSelected[0]]);

    useEffect(() => {
        /*
        arrow left：37
        arrow up：38
        arrow right：39
        arrow down：40
        */
        const onKeyDown = (e: JQuery.KeyDownEvent) => {
            if (e.keyCode == 17) {
                onlyFailRef.current = true;
            } else if (e.keyCode == 37) {
                // arrow left
                onNextMeasurement(onlyFailRef.current, true);
            } else if (e.keyCode == 39) {
                // arrow right
                onNextMeasurement(onlyFailRef.current, false);
            }
        }

        const onKeyUp = (e: JQuery.KeyUpEvent) => {
            if (e.keyCode == 17) {
                onlyFailRef.current = false;
            }
        }

        $(document).on('keydown', onKeyDown);
        $(document).on('keyup', onKeyUp);

        return () => {
            $(document).off('keydown', onKeyDown);
            $(document).off('keyup', onKeyUp);
        }
    }, [sMeasurement[0], sSelected[0]]);

    const dataReady = sSelected[0] && sMeasurementData[0] && sSelected[0].id == sLoadedMeasurementId[0];

    return <>
        <Segment loading={sLoading[0]} basic>
            <Header ref={titleRef}>
                <Header.Content>
                    {props.isSinglePage && <><FileStatusMark status={file.status} /> {file.product} / {file.order} / {file.testing_station_name} / </>}{_.last(file.file_path.split('/'))}
                    &nbsp;
                    <Button title={file.file_path} as="a" compact icon="download" onClick={e => {
                        e.preventDefault();
                        let $btn = $(e);
                        if (!$btn.is('a')) {
                            $btn = $btn.closest('a');
                        }

                        $btn.addClass('loading');
                        restRequest<string>(`testings/file_download_url/${file.id}`).then(downloadUrl => {
                            window.open(downloadUrl, '_blank');
                        }).finally(() => {
                            $btn.removeClass('loading');
                        });

                    }} size="mini"></Button>

                    {!props.isSinglePage && <Button onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();

                        const to_url = window.location.href.split(window.location.pathname)[0] + `/testing_file_visual/${file.id}/${sActiveTabIndex[0]}/${sSelected[0]?.measurement_key || 'none'}`;

                        navigator.clipboard.writeText(to_url).then(() => {
                            notificationControl.showSuccess('The URL have been copied!')
                        }).catch(err => {
                            dialogControl.alert(`The url is: ${to_url}`)
                        })
                    }} as="a" compact icon="share" size="mini"></Button>}
                </Header.Content>

                {!props.noOperation && !props.isSinglePage && currentUser.permissions.file_sync.delete_file.delete && <Button basic={!file.is_deleting} color="red" as="a" size="mini" compact onClick={(e) => {
                    $(e.target).addClass('loading');
                    restRequest(
                        `file_sync/deleting_testing_file/files/${file.id}`,
                        null,
                        file.is_deleting ? "delete" : 'post'
                    ).then(() => {
                        props.onFileChange && props.onFileChange({ ...file, is_deleting: !file.is_deleting })
                    }).finally(() => $(e.target).removeClass('loading'));
                }} className="right floated"><Icon name={file.is_deleting ? 'minus' : 'add'}></Icon>Delete</Button>}

                {!props.noOperation && !props.isSinglePage && currentUser.permissions.analyzed_unit.create && <Button color="blue" as="a" size="mini" compact onClick={() => {
                    waiveUnitEditModalControl.setOpen(true);
                }} className="right floated">Analyze</Button>}

            </Header>

            <TestResultMissingPointsContext.Provider value={sIsMissingPoints}>
                <Tab activeIndex={sActiveTabIndex[0]} onTabChange={(_, d) => {
                    sActiveTabIndex[1](d.activeIndex as number);

                    setParentSearchParameter((ps: any) => ({ ...ps, file_active_tab: d.activeIndex }))
                    if (d.activeIndex != 3) {
                        (window as any).unitPlotScrollY = -1;
                    }
                }} panes={[
                    { menuItem: 'General', render: () => <General file={file}></General> },
                    {
                        menuItem: 'Measurements', render: () => <Measurements measurements={sMeasurement[0]} selected={sSelected[0]} height={innerHeight} onSelect={(m, type) => {
                            sSelected[1](m);
                            if (type === 'data') {
                                sActiveTabIndex[1](2);
                            } else {
                                sActiveTabIndex[1](3);
                            }
                        }}></Measurements>
                    },
                    { menuItem: 'Result and Limits Data', render: () => dataReady ? <ResultAndLimit file={file} measurement={sSelected[0]!} data={sMeasurementData[0]} height={innerHeight} onNavigate={(m, t) => sActiveTabIndex[1](t)} onNextMeasurement={onNextMeasurement}></ResultAndLimit> : <div></div> },
                    { menuItem: 'Result and Limits Plot', render: () => dataReady ? <Plot file={file} measurement={sSelected[0]!} data={sMeasurementData[0]} height={innerHeight} onNavigate={(m, t) => sActiveTabIndex[1](t)} onNextMeasurement={onNextMeasurement}></Plot> : <div></div> }
                ]}></Tab>
            </TestResultMissingPointsContext.Provider>

            {sActiveTabIndex[0] !== 0 && <Button onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                showGeneralModalContrl.setOpen(true);
            }} as="a" size="mini" color="teal" circular style={{ position: 'fixed', right: '0.5rem', bottom: '0.5rem', opacity: 0.5 }}>General</Button>}

        </Segment>

        {props.isSinglePage || currentUser.permissions.analyzed_unit.create && <div onKeyUp={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}><AnalyzedUnitEditor modalControl={waiveUnitEditModalControl} fileInfo={{ ...((window as any).test_info || {}), serial: file.serial_number }} onFinish={result => {
            waiveUnitEditModalControl.setOpen(false);
        }}></AnalyzedUnitEditor></div>}

        <Modal {...showGeneralModalContrl.props} size="fullscreen">
            <Modal.Header>Genral Information</Modal.Header>
            <Modal.Content>
                <General file={file} />
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();

                    showGeneralModalContrl.setOpen(false);
                }} as="a">Close</Button>
            </Modal.Actions>
        </Modal>
    </>
}


function General(props: { file: TestingFile }) {
    const { file } = props;

    const [stockIns, stockInLoading] = useRequest<SaleData[]>(`testings/file/${file.id}/stock_in`, null, 'get');

    return <>
        <Table fixed basic>
            <Table.Body>
                <Table.Row>
                    <Table.Cell><b>Status</b></Table.Cell>
                    <Table.Cell><StatusMark status={file.status} /></Table.Cell>

                    <Table.Cell><b>Update Status</b></Table.Cell>
                    <Table.Cell>{file.update_status !== 0 ? <FileStatusMark status={file.update_status} /> : ''}</Table.Cell>


                    <Table.Cell><b>Date</b></Table.Cell>
                    <Table.Cell>{formatDateTime(file.date, true)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell><b>Total Time</b></Table.Cell>
                    <Table.Cell>{file.total_time}</Table.Cell>

                    <Table.Cell><b>Serial Number</b></Table.Cell>
                    <Table.Cell>{file.serial_number} {file.re_serial_number && <><br />{file.re_serial_number}(refurbishment)</>}</Table.Cell>

                    <Table.Cell><b>Order</b></Table.Cell>
                    <Table.Cell>{file.order}</Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell><b>Test Station</b></Table.Cell>
                    <Table.Cell>{file.testing_station_name} / <a className="ui link" target="_blank" href={`/inventory?testing_station_id=${file.testing_station_id}`}>{file.testing_station_id}</a></Table.Cell>

                    <Table.Cell><b>Production Manager</b></Table.Cell>
                    <Table.Cell>{file.production_manager}</Table.Cell>

                    <Table.Cell><b>Employee</b></Table.Cell>
                    <Table.Cell>{file.employee_id}</Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell><b>Programmer</b></Table.Cell>
                    <Table.Cell>{file.programmer_sn}</Table.Cell>

                    <Table.Cell><b>Firmware</b></Table.Cell>
                    <Table.Cell>{file.firmware_version}</Table.Cell>

                    <Table.Cell><b>Hardware</b></Table.Cell>
                    <Table.Cell>{file.hardware_version}</Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell><b>Product</b></Table.Cell>
                    <Table.Cell>{file.product}</Table.Cell>

                    <Table.Cell><b>Factory</b></Table.Cell>
                    <Table.Cell>{file.factory}</Table.Cell>

                    <Table.Cell><b>Sequence Parameters</b></Table.Cell>
                    <Table.Cell>{file.sequence_parameters && file.sequence_parameters.map((item: any) => `${item.name}: ${item.value}`).join(', ')}</Table.Cell>
                </Table.Row>
            </Table.Body>
        </Table>

        <Divider />

        <Table selectable striped>
            <Table.Header>
                <Table.Row>
                    <th>Warehouse Name</th>
                    <th>Warehouse Number</th>
                    <th>Warehousing Order NR</th>
                    <th>Model</th>
                    <th>Set Serial</th>
                    <th>Serial</th>
                    <th>Engraved</th>
                    <th>Order Number</th>
                    <th>Storage Type</th>
                    <th>Storage Date</th>
                    <th>Customer Id</th>
                    <th>Delivery Date</th>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {stockIns && stockIns.map(s => {
                    return <tr>
                        <td>{s.warehouse_name}</td>
                        <td>{s.warehouse_number}</td>
                        <td>{s.warehousing_order_nr}</td>
                        <td>{s.model}</td>
                        <td>{s.setserial}</td>
                        <td>{s.serial}</td>
                        <td>{s.engraved}</td>
                        <td>{s.order_number}</td>
                        <td>{s.storage_type}</td>
                        <td>{s.storage_date}</td>
                        <td>{s.customer_id}</td>
                        <td>{s.delivery_date}</td>
                    </tr>
                })}
            </Table.Body>
        </Table>
    </>
}

function Measurements(props: { measurements: Measurement[], selected?: Measurement, height: number, onSelect: (m: Measurement, type: 'data' | 'plot') => void }) {
    const rootDiv = useRef<HTMLDivElement>(null);
    const [foldedMeasurementIds, setFoldedMeasurementIds] = useState<string[]>([]);
    useEffect(() => {
        rootDiv.current!.scrollTop = (window as any)._measurementScrollTop || 0;
        const onScroll = () => {
            (window as any)._measurementScrollTop = rootDiv.current?.scrollTop || 0;
        };
        rootDiv.current?.addEventListener('scroll', onScroll);

        return () => rootDiv.current?.removeEventListener('scroll', onScroll);
    }, []);


    const currentPath = ['']

    return <div ref={rootDiv} style={{ height: props.height + 'px', overflow: 'auto' }} className="sticky-table">
        <Table celled striped selectable>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>Id</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Export File Name</Table.HeaderCell>
                    <Table.HeaderCell>Time</Table.HeaderCell>
                    <Table.HeaderCell>Attempts</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Limit Unit</Table.HeaderCell>
                    <Table.HeaderCell>Result Unit</Table.HeaderCell>
                    <Table.HeaderCell>Result and Limits</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {props.measurements.map(m => {
                    const parentId = m.parent_measurement_id || ''
                    if (parentId !== _.last(currentPath)) {
                        if (currentPath.includes(parentId)) {
                            while (currentPath.length > 0) {
                                if (_.last(currentPath) === parentId) {
                                    break
                                } else {
                                    currentPath.pop()
                                }
                            }
                        } else {
                            currentPath.push(parentId)
                        }
                    }

                    if (currentPath.find(p => p && foldedMeasurementIds.includes(p))) {
                        return undefined;
                    }

                    const isParentUnfolded = foldedMeasurementIds.includes(m.measurement_id);

                    return (<Table.Row key={m.id} active={props.selected && props.selected.id === m.id}>
                        <Table.Cell>{'-'.repeat(currentPath.length - 1)}{m.measurement_name}{m.measurement_type === 'BLOCK' && <> <Icon onClick={(e: any) => {
                            e.preventDefault();

                            if (isParentUnfolded) {
                                setFoldedMeasurementIds(ids => ids.filter(id => id !== m.measurement_id));
                            } else {
                                setFoldedMeasurementIds(ids => [...ids, m.measurement_id]);
                            }
                        }} color="blue" name={isParentUnfolded ? 'plus' : 'minus'} style={{ cursor: 'pointer' }}></Icon></>}</Table.Cell>
                        <Table.Cell>{m.measurement_id}</Table.Cell>
                        <Table.Cell>{m.measurement_type}</Table.Cell>
                        <Table.Cell>{m.export_file_name}</Table.Cell>
                        <Table.Cell>{m.measurement_time || 0}</Table.Cell>
                        <Table.Cell>{m.attempted || ''}</Table.Cell>
                        <Table.Cell><StatusMark status={m.measurement_status}></StatusMark></Table.Cell>
                        <Table.Cell>{m.limit_unit}</Table.Cell>
                        <Table.Cell>{m.result_unit}</Table.Cell>
                        <Table.Cell>
                            <Button size="mini" onClick={() => {
                                props.onSelect(m, 'data');
                            }}>data</Button>
                            <Button size="mini" onClick={() => {
                                props.onSelect(m, 'plot');
                            }}>plot</Button>
                        </Table.Cell>
                    </Table.Row>);
                })}
            </Table.Body>
        </Table>
    </div>
}


function ResultAndLimit(props: { file: TestingFile, measurement: Measurement, data: MeasurementData[], height: number, onNavigate: (measurement: Measurement, toTabIndex: number) => void, onNextMeasurement: (onlyError: boolean, isBack: boolean) => void }) {
    const { file, measurement, data } = props;

    const sIsMissingPoints = useContext(TestResultMissingPointsContext);
    
    let html: any;

    if (!data || data.length === 0) {
        html = <div>No Result</div>
    } else if (measurement.data_type === DATA_TYPE_OTHER || measurement.measurement_status === 3) {

        html = <div>
            {data && data.length > 0 && <Table selectable size="small" celled collapsing>
                <Table.Header>
                    <tr>
                        <th>Attempt</th>
                        <th>Channel</th>
                        <th>Lower</th>
                        <th>Value</th>
                        <th>Upper</th>
                    </tr>
                </Table.Header>
                <Table.Body>
                    {data.sort((a, b) => a.index > b.index ? -1 : 1).map((d, i) => {
                        let resultHTML: any;

                        const matchSecurityFile = d.result && (d.result as string).match(/lsf#(t?\d+)-(\S+)/)
                        let securityFileId: string | undefined;
                        let securityFileName: string | undefined;
                        if (matchSecurityFile) {
                            securityFileId = matchSecurityFile[1];
                            securityFileName = matchSecurityFile[2];
                        }

                        let resultHtml = securityFileId ? <Button title="download this security file" size="mini" onClick={(e) => {
                            e.preventDefault();
                            if (securityFileId!.startsWith('t')) {
                                window.open(`${hostAbsoluteUrl(`file_sync/security_file/${file.id}/${file.serial_number}/${securityFileId!.substring(1)}`)}`, '_blank')
                            } else {
                                window.open(`${hostAbsoluteUrl(`file_sync/security_file/${securityFileId!}`)}`, '_blank')
                            }
                        }}>{securityFileName}</Button> : <div>{d.result}</div>;

                        return <tr key={i}>
                            <td>{d.index}</td>
                            <td>{d.channel}</td>
                            <td>{d.limits.lower || ''}</td>
                            <td>{resultHtml}</td>
                            <td>{d.limits.upper || ''}</td>
                        </tr>
                    })}
                </Table.Body>
            </Table>}
        </div>;
    } else if (measurement.data_type == DATA_TYPE_SCALAR || measurement.data_type == DATA_TYPE_ARRAY) {
        html = <ScalarOrArrayDataComponent data={data} measurement={measurement}></ScalarOrArrayDataComponent>
    } else if (measurement.data_type == DATA_TYPE_VECTOR) {
        html = <VectorDataComponent measurement={measurement} data={data}></VectorDataComponent>
    }

    return <>
        <Header attached="top">{measurement?.measurement_name} {measurement?.export_file_name ? <sub style={{ marginLeft: '1rem' }}>{measurement.export_file_name}</sub> : ''}
            <StatusMark status={measurement?.measurement_status}></StatusMark>

            {sIsMissingPoints[0] && <Message style={{ margin: '0 5rem', padding: '3px 1rem', fontWeight: 'normal' }} error basic compact>This data doesn't include all the graph points. For all points, please <Button
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    let $btn = $(e);
                    if (!$btn.is('a')) {
                        $btn = $btn.closest('a');
                    }

                    $btn.addClass('loading');
                    restRequest<string>(`testings/file_download_url/${file.id}`).then(downloadUrl => {
                        window.open(downloadUrl+'&scrollto='+measurement.export_file_name + '.csv', '_blank');
                    }).finally(() => {
                        $btn.removeClass('loading');
                    });

                }} compact secondary size="mini">Open Sequence Result</Button> and check the <b>{measurement.export_file_name + '.csv'}</b></Message>}

            <Button onClick={() => props.onNavigate(measurement, 3)} className="right floated" size="mini" compact>Plot</Button>
            <Button onClick={() => props.onNavigate(measurement, 1)} className="right floated" size="mini" compact>Measurements</Button>

            <Button.Group size="mini" className="right floated" color="red" style={{ marginRight: '3rem', marginLeft: '1rem' }}>
                <Button onClick={() => props.onNextMeasurement(true, true)} size="mini" compact icon="arrow left" title="Previous Fail Measurement (press ctrl + <-)"></Button>
                <Button onClick={() => props.onNextMeasurement(true, false)} size="mini" compact icon="arrow right" title="Next Fail Measurement (press ctrl + ->)"></Button>
            </Button.Group>

            <Button.Group size="mini" className="right floated">
                <Button onClick={() => props.onNextMeasurement(false, true)} size="mini" compact icon="arrow left" title="Previous Measurement (press <-)"></Button>
                <Button onClick={() => props.onNextMeasurement(false, false)} size="mini" compact icon="arrow right" title="Next Measurement  (press ->)"></Button>
            </Button.Group>
        </Header>
        <Segment style={{ height: (props.height) + 'px', overflow: 'auto' }} attached="bottom">
            {html}
        </Segment>

    </>
}


function Plot(props: { file: TestingFile, measurement: Measurement, data: MeasurementData[], height: number, onNavigate: (measurement: Measurement, toTabIndex: number) => void, onNextMeasurement: (onlyError: boolean, isBack: boolean) => void }) {
    const {file, measurement, data } = props;
    const sIsMissingPoints = useContext(TestResultMissingPointsContext);


    useLayoutEffect(() => {
        const onScroll = () => {
            (window as any).unitPlotScrollY = window.scrollY;
        };
        $(window).on('scroll', onScroll);

        return () => {
            $(window).off('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        if (data && data.length > 0) {
            nextTick(() => {
                if ((window as any).unitPlotScrollY > 0) {
                    window.scrollTo(window.scrollX, (window as any).unitPlotScrollY);
                }
            });
        }
    }, [data])

    let html: any;

    if (!data || data.length === 0) {
        html = <div>No Result</div>
    } else {
        if (measurement.data_type === DATA_TYPE_OTHER || measurement.measurement_status === 3) {
            html = <div>
                {data && data.length > 0 && <Table selectable size="small" celled collapsing>
                    <Table.Header>
                        <tr>
                            <th>Channel</th>
                            <th>Lower</th>
                            <th>Value</th>
                            <th>Upper</th>
                        </tr>
                    </Table.Header>
                    <Table.Body>
                        {data.map((d, i) => <tr key={i}>
                            <td>{d.channel}</td>
                            <td>{d.limits.lower || ''}</td>
                            <td>{d.result || ''}</td>
                            <td>{d.limits.upper || ''}</td>
                        </tr>)}
                    </Table.Body>
                </Table>}
            </div>;
        } else if (measurement.data_type == DATA_TYPE_SCALAR) {
            html = <ScalarPlotComponent data={data} measurement={measurement}></ScalarPlotComponent>
        } else if (measurement.data_type == DATA_TYPE_ARRAY) {
            html = <ArrayPlotComponent data={data}></ArrayPlotComponent>
        } else if (measurement.data_type == DATA_TYPE_VECTOR) {
            html = <VectorPlotComponent measurement={measurement} data={data} height={props.height} ></VectorPlotComponent>
        }
    }

    return <>
        <Header attached="top">{measurement?.measurement_name} {measurement?.export_file_name ? <sub style={{ marginLeft: '1rem' }}>{measurement.export_file_name}</sub> : ''}
            <StatusMark status={measurement?.measurement_status}></StatusMark>
            {sIsMissingPoints[0] && <Message style={{ margin: '0 5rem', padding: '3px 1rem', fontWeight: 'normal' }} error basic compact>This plot doesn't include all the graph points. For all points, please <Button
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    let $btn = $(e);
                    if (!$btn.is('a')) {
                        $btn = $btn.closest('a');
                    }

                    $btn.addClass('loading');
                    restRequest<string>(`testings/file_download_url/${file.id}`).then(downloadUrl => {
                        window.open(downloadUrl+'&scrollto='+measurement.export_file_name + '.png', '_blank');
                    }).finally(() => {
                        $btn.removeClass('loading');
                    });

                }} compact secondary size="mini">Open Sequence Result</Button> and check the <b>{measurement.export_file_name + '.png'}</b></Message>}

            <Button onClick={() => props.onNavigate(measurement, 2)} className="right floated" size="mini" compact>Data</Button>
            <Button onClick={() => props.onNavigate(measurement, 1)} className="right floated" size="mini" compact>Measurements</Button>

            <Button.Group size="mini" className="right floated" color="red" style={{ marginRight: '3rem', marginLeft: '1rem' }}>
                <Button onClick={() => props.onNextMeasurement(true, true)} size="mini" compact icon="arrow left" title="Previous Fail Measurement  (press ctrl + <-)"></Button>
                <Button onClick={() => props.onNextMeasurement(true, false)} size="mini" compact icon="arrow right" title="Next Fail Measurement  (press ctrl + ->)"></Button>
            </Button.Group>

            <Button.Group size="mini" className="right floated">
                <Button onClick={() => props.onNextMeasurement(false, true)} size="mini" compact icon="arrow left" title="Previous Measurement  (press <-)"></Button>
                <Button onClick={() => props.onNextMeasurement(false, false)} size="mini" compact icon="arrow right" title="Next Measurement  (press ->)"></Button>
            </Button.Group>
        </Header>
        <Segment style={{ height: props.height + 'px', overflow: 'auto' }} attached="bottom">
            {html}
        </Segment>
    </>
}